import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { Users, Calendar as CalendarIcon, TrendingUp, Search, Plus, Edit, Trash2, Key, X, Check, BarChart3, Trophy, QrCode, Link, Copy, Share2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import QRCheckInDisplay from './QRCheckInDisplay';
import Analytics from './Analytics';
import PRTracking from './PRTracking';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CoachDashboard({ user }) {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [wods, setWods] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // User Management States
  const [showUserForm, setShowUserForm] = useState(false);
  const [userFormData, setUserFormData] = useState({
    name: '',
    username: '',
    password: '',
    role: 'athlete'
  });
  const [deleteUserDialog, setDeleteUserDialog] = useState(null);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [editUserDialog, setEditUserDialog] = useState(null);
  const [editUserData, setEditUserData] = useState({ name: '', role: '', birthday: '', username: '' });
  const [selectedUserAttendance, setSelectedUserAttendance] = useState(null);
  const [userAttendance, setUserAttendance] = useState([]);
  const [classView, setClassView] = useState('upcoming');

  // Onboard modal state
  const [onboardModal, setOnboardModal] = useState(null); // { user, inviteUrl }
  const [onboardTab, setOnboardTab] = useState('qr'); // 'qr' | 'link'
  const [onboardLoading, setOnboardLoading] = useState(false);

  // WOD Management States
  const [showWODForm, setShowWODForm] = useState(false);
  const [editingWOD, setEditingWOD] = useState(null);
  const [wodFormData, setWodFormData] = useState({
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    pr_movement_id: ''
  });
  const [deleteWODDialog, setDeleteWODDialog] = useState(null);
  const [movements, setMovements] = useState([]);
  const [showNewMovementInline, setShowNewMovementInline] = useState(false);
  const [inlineMovement, setInlineMovement] = useState({ name: '', unit: 'kg' });

  // Class Management States
  const [showClassForm, setShowClassForm] = useState(false);
  const [classFormMode, setClassFormMode] = useState('single'); // single, recurring, edit
  const [editingClassId, setEditingClassId] = useState(null);
  const [classFormData, setClassFormData] = useState({
    title: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '10:00',
    max_capacity: 15
  });
  const [recurringFormData, setRecurringFormData] = useState({
    title: '',
    days_of_week: [],
    start_time: '09:00',
    end_time: '10:00',
    max_capacity: 15,
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });
  const [deleteClassDialog, setDeleteClassDialog] = useState(null);
  const [classBookings, setClassBookings] = useState({});
  const [showingBookings, setShowingBookings] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [usersRes, wodsRes, classesRes, movRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/wods`),
        axios.get(`${API}/classes`),
        axios.get(`${API}/movements`)
      ]);
      setUsers(usersRes.data);
      setWods(wodsRes.data);
      setClasses(classesRes.data);
      setMovements(movRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // User Management Functions
  const createUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, userFormData);
      toast.success('User created successfully!');
      setShowUserForm(false);
      setUserFormData({ name: '', username: '', password: '', role: 'athlete' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const deleteUser = async () => {
    try {
      await axios.delete(`${API}/users/${deleteUserDialog}`);
      toast.success('User deleted successfully');
      setDeleteUserDialog(null);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const resetPassword = async () => {
    try {
      await axios.post(`${API}/users/${resetPasswordDialog}/reset-password`, {
        new_password: newPassword
      });
      toast.success('Password reset successfully');
      setResetPasswordDialog(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const fetchUserAttendance = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}/attendance`);
      setUserAttendance(response.data);
      setSelectedUserAttendance(userId);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Failed to load attendance');
      }
    }
  };

  const startEditUser = (u) => {
    setEditUserData({ name: u.name, role: u.role, birthday: u.birthday || '', username: u.username || '' });
    setEditUserDialog(u.id);
  };

  const generateInviteLink = async (u) => {
    setOnboardLoading(true);
    try {
      const res = await axios.post(`${API}/auth/invite-token`, { user_id: u.id });
      const inviteUrl = `${window.location.origin}/invite?token=${res.data.token}`;
      setOnboardModal({ user: u, inviteUrl });
      setOnboardTab('qr');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invite link');
    } finally {
      setOnboardLoading(false);
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(onboardModal.inviteUrl);
    toast.success('Link copied to clipboard!');
  };

  const shareWhatsApp = () => {
    const msg = encodeURIComponent(
      `Hi ${onboardModal.user.name}! Your R&R Elite Fitness account is ready. Tap the link to set your password and log in:\n\n${onboardModal.inviteUrl}`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  const saveEditUser = async () => {
    try {
      await axios.put(`${API}/users/${editUserDialog}`, editUserData);
      toast.success('User updated!');
      setEditUserDialog(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const unmarkAttended = async (bookingId) => {
    try {
      await axios.post(`${API}/bookings/${bookingId}/unmark-attended`);
      toast.success('Attendance unmarked');
      if (showingBookings) {
        const response = await axios.get(`${API}/classes/${showingBookings}/bookings`);
        setClassBookings({ ...classBookings, [showingBookings]: response.data });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unmark');
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const upcomingClasses = classes.filter(c => c.date >= today);
  const archivedClasses = classes.filter(c => c.date < today);

  // Group archived classes by month
  const archivedByMonth = {};
  archivedClasses.forEach(cls => {
    const d = new Date(cls.date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    if (!archivedByMonth[key]) archivedByMonth[key] = { label, classes: [] };
    archivedByMonth[key].classes.push(cls);
  });
  const archivedMonths = Object.keys(archivedByMonth).sort().reverse();
  const [expandedMonth, setExpandedMonth] = useState(null);

  const roleOrder = { head_coach: 0, coach: 1, athlete: 2 };
  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.username || u.email || '').toLowerCase().includes(searchQuery.toLowerCase())
  ).sort((a, b) => {
    const roleDiff = (roleOrder[a.role] ?? 3) - (roleOrder[b.role] ?? 3);
    if (roleDiff !== 0) return roleDiff;
    return a.name.localeCompare(b.name);
  });

  // WOD Management Functions
  const createOrUpdateWOD = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...wodFormData };
      if (!payload.pr_movement_id) payload.pr_movement_id = null;
      if (editingWOD) {
        await axios.put(`${API}/wods/${editingWOD}`, payload);
        toast.success('WOD updated successfully!');
      } else {
        await axios.post(`${API}/wods`, payload);
        toast.success('WOD created successfully!');
      }
      setShowWODForm(false);
      setEditingWOD(null);
      setWodFormData({ title: '', description: '', date: new Date().toISOString().split('T')[0], pr_movement_id: '' });
      setShowNewMovementInline(false);
      setInlineMovement({ name: '', unit: 'kg' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save WOD');
    }
  };

  const startEditWOD = (wod) => {
    setEditingWOD(wod.id);
    setWodFormData({
      title: wod.title,
      description: wod.description,
      date: wod.date,
      pr_movement_id: wod.pr_movement_id || ''
    });
    setShowNewMovementInline(false);
    setShowWODForm(true);
  };

  const deleteWOD = async () => {
    try {
      await axios.delete(`${API}/wods/${deleteWODDialog}`);
      toast.success('WOD deleted successfully');
      setDeleteWODDialog(null);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete WOD');
    }
  };

  const handleDateClick = (arg) => {
    setWodFormData({
      title: '',
      description: '',
      date: arg.dateStr
    });
    setEditingWOD(null);
    setShowWODForm(true);
  };

  const handleEventClick = (info) => {
    const wod = wods.find(w => w.id === info.event.id);
    if (wod) {
      startEditWOD(wod);
    }
  };

  const calendarEvents = wods.map(wod => ({
    id: wod.id,
    title: wod.title,
    date: wod.date,
    backgroundColor: '#CCFF00',
    borderColor: '#CCFF00',
    textColor: '#000000'
  }));

  const cancelWODEdit = () => {
    setShowWODForm(false);
    setEditingWOD(null);
    setWodFormData({ title: '', description: '', date: new Date().toISOString().split('T')[0], pr_movement_id: '' });
    setShowNewMovementInline(false);
    setInlineMovement({ name: '', unit: 'kg' });
  };

  const createInlineMovement = async () => {
    if (!inlineMovement.name.trim()) return;
    try {
      const res = await axios.post(`${API}/movements`, {
        name: inlineMovement.name.trim(),
        unit: inlineMovement.unit,
        lower_is_better: ['seconds', 'minutes'].includes(inlineMovement.unit)
      });
      setMovements(prev => [...prev, res.data]);
      setWodFormData(prev => ({ ...prev, pr_movement_id: res.data.id }));
      setShowNewMovementInline(false);
      setInlineMovement({ name: '', unit: 'kg' });
      toast.success(`"${res.data.name}" created and linked!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create movement');
    }
  };

  // Class Management Functions
  const validateClassTimes = (start, end) => {
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    return (eh * 60 + em) > (sh * 60 + sm);
  };

  const createClass = async (e) => {
    e.preventDefault();
    if (!validateClassTimes(classFormData.start_time, classFormData.end_time)) {
      toast.error(`End time (${classFormData.end_time}) must be after start time (${classFormData.start_time})`);
      return;
    }
    try {
      if (editingClassId) {
        await axios.put(`${API}/classes/${editingClassId}`, classFormData);
        toast.success('Class updated!');
      } else {
        await axios.post(`${API}/classes`, classFormData);
        toast.success('Class scheduled!');
      }
      setShowClassForm(false);
      setEditingClassId(null);
      setClassFormData({ title: '', date: new Date().toISOString().split('T')[0], start_time: '09:00', end_time: '10:00', max_capacity: 15 });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save class');
    }
  };

  const createRecurringClasses = async (e) => {
    e.preventDefault();
    if (!validateClassTimes(recurringFormData.start_time, recurringFormData.end_time)) {
      toast.error(`End time (${recurringFormData.end_time}) must be after start time (${recurringFormData.start_time})`);
      return;
    }
    try {
      const result = await axios.post(`${API}/classes/recurring`, recurringFormData);
      toast.success(`${result.data.count} classes created!`);
      setShowClassForm(false);
      setRecurringFormData({ title: '', days_of_week: [], start_time: '09:00', end_time: '10:00', max_capacity: 15, start_date: new Date().toISOString().split('T')[0], end_date: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create classes');
    }
  };

  const startEditClass = (cls) => {
    setEditingClassId(cls.id);
    setClassFormData({ title: cls.title, date: cls.date, start_time: cls.start_time, end_time: cls.end_time, max_capacity: cls.max_capacity });
    setClassFormMode('edit');
    setShowClassForm(true);
  };

  const deleteClass = async () => {
    try {
      await axios.delete(`${API}/classes/${deleteClassDialog}`);
      toast.success('Class cancelled successfully');
      setDeleteClassDialog(null);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to cancel class');
    }
  };

  const toggleLateBooking = async (cls) => {
    try {
      const res = await axios.patch(`${API}/classes/${cls.id}/toggle-late-booking`);
      setClasses(prev => prev.map(c => c.id === cls.id
        ? { ...c, late_booking_override: res.data.late_booking_override }
        : c
      ));
      toast.success(res.data.late_booking_override
        ? 'Late booking opened — members can book within 45 min'
        : 'Booking cutoff restored to 45 minutes'
      );
    } catch {
      toast.error('Failed to update booking setting');
    }
  };

  const fetchClassBookings = async (classId) => {
    if (showingBookings === classId) {
      setShowingBookings(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/classes/${classId}/bookings`);
      setClassBookings({ ...classBookings, [classId]: response.data });
      setShowingBookings(classId);
    } catch (error) {
      toast.error('Failed to load bookings');
    }
  };

  const markAttended = async (bookingId) => {
    try {
      await axios.post(`${API}/bookings/${bookingId}/mark-attended`);
      toast.success('Marked as attended!');
      // Refresh the bookings for the current class
      if (showingBookings) {
        const response = await axios.get(`${API}/classes/${showingBookings}/bookings`);
        setClassBookings({ ...classBookings, [showingBookings]: response.data });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark attendance');
    }
  };

  const handleStartTimeChange = (value) => {
    const [hours, minutes] = value.split(':');
    const endHours = (parseInt(hours) + 1).toString().padStart(2, '0');
    setClassFormData({
      ...classFormData,
      start_time: value,
      end_time: `${endHours}:${minutes}`
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#CCFF00] text-xl font-heading uppercase">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-12" data-testid="coach-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-5xl md:text-7xl font-heading font-black uppercase tracking-tighter text-[#FAFAFA] mb-2">
            Coach Dashboard
          </h1>
          <p className="text-lg text-[#A1A1AA] font-medium">Manage users, WODs, and classes</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex w-full overflow-x-auto bg-[#18181B] border border-[#27272A] mb-8 gap-1 p-1" data-testid="coach-tabs">
            <TabsTrigger
              value="users"
              data-testid="tab-users"
              className="flex-1 min-w-0 data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black uppercase tracking-wide font-bold text-xs px-2 py-2"
            >
              <Users className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Users</span>
            </TabsTrigger>
            <TabsTrigger
              value="wods"
              data-testid="tab-wods"
              className="flex-1 min-w-0 data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black uppercase tracking-wide font-bold text-xs px-2 py-2"
            >
              <TrendingUp className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">WODs</span>
            </TabsTrigger>
            <TabsTrigger
              value="classes"
              data-testid="tab-classes"
              className="flex-1 min-w-0 data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black uppercase tracking-wide font-bold text-xs px-2 py-2"
            >
              <CalendarIcon className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Classes</span>
            </TabsTrigger>
            <TabsTrigger
              value="analytics"
              data-testid="tab-analytics"
              className="flex-1 min-w-0 data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black uppercase tracking-wide font-bold text-xs px-2 py-2"
            >
              <BarChart3 className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Analytics</span>
            </TabsTrigger>
            <TabsTrigger
              value="prs"
              data-testid="tab-prs"
              className="flex-1 min-w-0 data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black uppercase tracking-wide font-bold text-xs px-2 py-2"
            >
              <Trophy className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">PRs</span>
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" data-testid="users-content">
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="relative flex-1 w-full sm:max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#52525B]" />
                  <Input
                    placeholder="Search users..."
                    data-testid="search-users"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-[#09090B] border-[#27272A] text-white focus:border-[#CCFF00] h-12"
                  />
                </div>
                <Button
                  onClick={() => setShowUserForm(true)}
                  data-testid="add-user-button"
                  className="bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#B3E600] rounded-sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredUsers.map((u) => (
                  <Card key={u.id} className="bg-[#18181B] border-[#27272A] p-5" data-testid={`user-card-${u.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-xl font-heading font-bold uppercase text-[#FAFAFA]">{u.name}</h3>
                        <p className="text-sm text-[#A1A1AA]">{u.username || u.email}</p>
                      </div>
                      <span className={`text-xs font-bold px-2 py-1 rounded-sm uppercase ${
                        u.role === 'head_coach' ? 'bg-[#CCFF00] text-black' :
                        u.role === 'coach' ? 'bg-[#2563EB] text-white' : 'bg-[#27272A] text-[#FAFAFA]'
                      }`}>
                        {u.role === 'head_coach' ? 'Head Coach' : u.role}
                      </span>
                    </div>
                    {u.birthday && (
                      <p className="text-xs text-[#A1A1AA] mb-1" data-testid={`birthday-${u.id}`}>
                        Birthday: {(() => { const [m,d] = u.birthday.split('-'); const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${parseInt(d)} ${months[parseInt(m)-1]}`; })()}
                      </p>
                    )}
                    {u.last_attendance_date ? (
                      <p className="text-xs text-[#A1A1AA] mb-3" data-testid={`last-attendance-${u.id}`}>
                        Last attended: {new Date(u.last_attendance_date).toLocaleDateString()}
                      </p>
                    ) : (
                      u.role === 'athlete' && (
                        <p className="text-xs text-[#52525B] mb-3" data-testid={`last-attendance-${u.id}`}>
                          No attendance yet
                        </p>
                      )
                    )}
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        onClick={() => fetchUserAttendance(u.id)}
                        data-testid={`view-attendance-${u.id}`}
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-[#CCFF00]"
                      >
                        View History
                      </Button>
                      <Button
                        onClick={() => startEditUser(u)}
                        data-testid={`edit-user-${u.id}`}
                        size="sm"
                        variant="outline"
                        className="text-xs border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-[#CCFF00]"
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button
                        onClick={() => setResetPasswordDialog(u.id)}
                        data-testid={`reset-password-${u.id}`}
                        size="sm"
                        variant="outline"
                        className="text-xs border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-[#CCFF00]"
                      >
                        <Key className="w-3 h-3" />
                      </Button>
                      <Button
                        onClick={() => setDeleteUserDialog(u.id)}
                        data-testid={`delete-user-${u.id}`}
                        size="sm"
                        variant="outline"
                        className="text-xs border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-[#EF4444]"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                    {/* Onboard button - full width below action row */}
                    <Button
                      onClick={() => generateInviteLink(u)}
                      data-testid={`onboard-user-${u.id}`}
                      size="sm"
                      disabled={onboardLoading}
                      className="w-full mt-2 text-xs bg-[#CCFF00]/10 border border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/20 font-bold uppercase tracking-wider rounded-sm"
                    >
                      <Share2 className="w-3 h-3 mr-1.5" />
                      Onboard Member
                    </Button>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* WODs Tab */}
          <TabsContent value="wods" data-testid="wods-content">
            <div className="space-y-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-heading font-bold uppercase text-[#FAFAFA]">WOD Calendar</h2>
                <Button
                  onClick={() => setShowWODForm(true)}
                  data-testid="add-wod-button"
                  className="bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#B3E600] rounded-sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create WOD
                </Button>
              </div>

              <Card className="bg-[#18181B] border-[#27272A] p-6" data-testid="wod-calendar">
                <FullCalendar
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  events={calendarEvents}
                  dateClick={handleDateClick}
                  eventClick={handleEventClick}
                  height="auto"
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth'
                  }}
                />
              </Card>

              <p className="text-xs text-[#52525B] text-center">Click on a date to create a WOD, or click on an event to edit/delete it</p>
            </div>
          </TabsContent>

          {/* Classes Tab */}
          <TabsContent value="classes" data-testid="classes-content">
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h2 className="text-2xl font-heading font-bold uppercase text-[#FAFAFA]">Manage Classes</h2>
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button
                    onClick={() => { setClassFormMode('single'); setEditingClassId(null); setShowClassForm(true); }}
                    data-testid="add-class-button"
                    className="flex-1 sm:flex-none bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#B3E600] rounded-sm text-xs sm:text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Single
                  </Button>
                  <Button
                    onClick={() => { setClassFormMode('recurring'); setShowClassForm(true); }}
                    data-testid="add-recurring-class-button"
                    variant="outline"
                    className="flex-1 sm:flex-none border-[#CCFF00] text-[#CCFF00] font-bold uppercase tracking-wider hover:bg-[#CCFF00]/10 rounded-sm text-xs sm:text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Recurring
                  </Button>
                </div>
              </div>

              {/* QR Check-In */}
              <QRCheckInDisplay />

              <div className="space-y-3">
                {/* Class View Toggle */}
                <div className="flex gap-2 mb-4">
                  <Button
                    onClick={() => setClassView('upcoming')}
                    size="sm"
                    className={classView === 'upcoming' ? 'bg-[#CCFF00] text-black font-bold' : 'bg-[#27272A] text-[#A1A1AA]'}
                  >
                    Upcoming ({upcomingClasses.length})
                  </Button>
                  <Button
                    onClick={() => setClassView('archived')}
                    size="sm"
                    className={classView === 'archived' ? 'bg-[#CCFF00] text-black font-bold' : 'bg-[#27272A] text-[#A1A1AA]'}
                  >
                    Archived ({archivedClasses.length})
                  </Button>
                </div>

                {classView === 'upcoming' && upcomingClasses.map((cls) => (
                  <Card key={cls.id} className="bg-[#18181B] border-[#27272A] p-5" data-testid={`class-item-${cls.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="text-xl font-heading font-bold uppercase text-[#FAFAFA] mb-1">{cls.title}</h4>
                        <p className="text-sm text-[#A1A1AA] mb-1">
                          {new Date(cls.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} {'\u2022'} {cls.start_time} - {cls.end_time}
                        </p>
                        {cls.created_by_name && <p className="text-xs text-[#52525B] mb-2">by {cls.created_by_name}</p>}
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-[#FAFAFA]">{cls.booked_count} / {cls.max_capacity} Booked</span>
                          <span className={`w-2 h-2 rounded-full ${cls.booked_count >= cls.max_capacity ? 'bg-[#EF4444]' : 'bg-[#22C55E]'}`}></span>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {/* Late Booking Override toggle */}
                        <Button
                          onClick={() => toggleLateBooking(cls)}
                          data-testid={`toggle-late-booking-${cls.id}`}
                          size="sm"
                          variant="outline"
                          title={cls.late_booking_override ? 'Click to restore 45-min cutoff' : 'Click to allow last-minute bookings'}
                          className={`text-xs border rounded-sm transition-all ${
                            cls.late_booking_override
                              ? 'border-[#CCFF00]/50 text-[#CCFF00] bg-[#CCFF00]/10 hover:bg-[#CCFF00]/20'
                              : 'border-[#27272A] text-[#52525B] hover:border-[#CCFF00]/30 hover:text-[#A1A1AA]'
                          }`}
                        >
                          {cls.late_booking_override ? 'Late Booking: ON' : 'Open Late Booking'}
                        </Button>
                        <Button onClick={() => fetchClassBookings(cls.id)} data-testid={`view-bookings-${cls.id}`} size="sm" variant="outline" className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-[#CCFF00]">
                          <Users className="w-4 h-4 mr-2" />Bookings
                        </Button>
                        <Button onClick={() => startEditClass(cls)} data-testid={`edit-class-${cls.id}`} size="sm" variant="outline" className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-[#CCFF00]">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button onClick={() => setDeleteClassDialog(cls.id)} data-testid={`cancel-class-${cls.id}`} size="sm" variant="outline" className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-[#EF4444]">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {showingBookings === cls.id && classBookings[cls.id] && (
                      <div className="mt-4 p-4 bg-[#09090B] rounded-sm border border-[#27272A]" data-testid={`bookings-list-${cls.id}`}>
                        <h5 className="text-sm font-semibold text-[#FAFAFA] uppercase tracking-wide mb-3">Booked Athletes</h5>
                        {classBookings[cls.id].length === 0 ? (
                          <p className="text-[#A1A1AA] text-sm">No bookings yet</p>
                        ) : (
                          <ul className="space-y-3">
                            {classBookings[cls.id].map((booking) => (
                              <li key={booking.booking_id} className="flex items-center justify-between p-3 bg-[#18181B] rounded-sm border border-[#27272A]">
                                <div className="flex items-center gap-3">
                                  <span className={`w-2 h-2 rounded-full ${booking.checked_in ? 'bg-[#22C55E]' : 'bg-[#CCFF00]'}`}></span>
                                  <div>
                                    <p className="text-[#FAFAFA] font-semibold">{booking.user.name}</p>
                                    <p className="text-xs text-[#A1A1AA]">{booking.user.username || booking.user.email}</p>
                                    {booking.booked_at && <p className="text-xs text-[#52525B] mt-0.5">Booked: {new Date(booking.booked_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>}
                                    {booking.guest_count > 0 && <p className="text-xs text-[#CCFF00] mt-1">+ {booking.guest_count} guest{booking.guest_count > 1 ? 's' : ''}</p>}
                                  </div>
                                </div>
                                {booking.checked_in ? (
                                  <Button onClick={() => unmarkAttended(booking.booking_id)} size="sm" variant="outline" className="border-[#22C55E]/30 text-[#22C55E] hover:bg-[#22C55E]/10 hover:text-[#EF4444] hover:border-[#EF4444]/30 text-xs">
                                    <Check className="w-3 h-3 mr-1" />Attended
                                  </Button>
                                ) : (
                                  <Button onClick={() => markAttended(booking.booking_id)} size="sm" className="bg-[#22C55E] text-white hover:bg-[#16A34A] font-bold uppercase tracking-wide text-xs">
                                    <Check className="w-3 h-3 mr-1" />Mark Attended
                                  </Button>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </Card>
                ))}
                {classView === 'upcoming' && upcomingClasses.length === 0 && (
                  <p className="text-[#A1A1AA] text-center py-8">No upcoming classes</p>
                )}

                {/* Archived — grouped by month */}
                {classView === 'archived' && (
                  archivedMonths.length === 0 ? (
                    <p className="text-[#A1A1AA] text-center py-8">No archived classes</p>
                  ) : (
                    archivedMonths.map((monthKey) => {
                      const month = archivedByMonth[monthKey];
                      const isOpen = expandedMonth === monthKey;
                      return (
                        <div key={monthKey} className="border border-[#27272A] rounded-sm overflow-hidden">
                          <button
                            onClick={() => setExpandedMonth(isOpen ? null : monthKey)}
                            className="w-full flex items-center justify-between p-4 bg-[#18181B] hover:bg-[#1F1F23] transition-colors"
                          >
                            <span className="text-base font-heading font-bold uppercase text-[#FAFAFA]">{month.label}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-[#A1A1AA]">{month.classes.length} classes</span>
                              <span className="text-[#A1A1AA]">{isOpen ? '\u25B2' : '\u25BC'}</span>
                            </div>
                          </button>
                          {isOpen && (
                            <div className="space-y-3 p-3 bg-[#09090B]">
                              {month.classes.map((cls) => (
                                <Card key={cls.id} className="bg-[#18181B]/50 border-[#1F1F23] p-4">
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div className="flex-1">
                                      <h4 className="text-base font-heading font-bold uppercase text-[#A1A1AA]">{cls.title}</h4>
                                      <p className="text-xs text-[#52525B]">
                                        {new Date(cls.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} {'\u2022'} {cls.start_time} - {cls.end_time}
                                      </p>
                                      {cls.created_by_name && <p className="text-xs text-[#52525B]">by {cls.created_by_name}</p>}
                                      <span className="text-xs text-[#52525B]">{cls.booked_count}/{cls.max_capacity} booked</span>
                                    </div>
                                    <Button onClick={() => fetchClassBookings(cls.id)} size="sm" variant="outline" className="border-[#27272A] text-[#A1A1AA] hover:text-[#CCFF00] text-xs">
                                      <Users className="w-3 h-3 mr-1.5" />
                                      {showingBookings === cls.id ? 'Hide' : 'View'} Members
                                    </Button>
                                  </div>
                                  {showingBookings === cls.id && classBookings[cls.id] && (
                                    <div className="mt-3 p-3 bg-[#09090B] rounded-sm border border-[#27272A]">
                                      {classBookings[cls.id].length === 0 ? (
                                        <p className="text-[#52525B] text-xs">No bookings</p>
                                      ) : (
                                        <ul className="space-y-2">
                                          {classBookings[cls.id].map((booking) => (
                                            <li key={booking.booking_id} className="flex items-center justify-between p-2.5 bg-[#18181B] rounded-sm border border-[#27272A]">
                                              <div className="flex items-center gap-2">
                                                <span className={`w-2 h-2 rounded-full ${booking.checked_in ? 'bg-[#22C55E]' : 'bg-[#CCFF00]'}`}></span>
                                                <div>
                                                  <p className="text-sm text-[#FAFAFA] font-semibold">{booking.user.name}</p>
                                                  <p className="text-xs text-[#52525B]">{booking.user.username || booking.user.email}</p>
                                                </div>
                                              </div>
                                              {booking.checked_in ? (
                                                <Button onClick={() => unmarkAttended(booking.booking_id)} size="sm" variant="outline" className="border-[#22C55E]/30 text-[#22C55E] hover:text-[#EF4444] hover:border-[#EF4444]/30 text-xs h-7">
                                                  <Check className="w-3 h-3 mr-1" />Attended
                                                </Button>
                                              ) : (
                                                <Button onClick={() => markAttended(booking.booking_id)} size="sm" className="bg-[#22C55E] text-white hover:bg-[#16A34A] font-bold text-xs h-7">
                                                  <Check className="w-3 h-3 mr-1" />Mark
                                                </Button>
                                              )}
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  )}
                                </Card>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )
                )}
              </div>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" data-testid="analytics-content">
            <Analytics />
          </TabsContent>

          {/* PRs Tab */}
          <TabsContent value="prs" data-testid="prs-content">
            <PRTracking user={user} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Onboard Member Modal */}
      {onboardModal && (
        <Dialog open={!!onboardModal} onOpenChange={() => setOnboardModal(null)}>
          <DialogContent className="bg-[#18181B] border-[#27272A] max-w-sm" data-testid="onboard-modal">
            <DialogHeader>
              <DialogTitle className="text-[#FAFAFA] font-heading uppercase text-lg">
                Onboard {onboardModal.user.name}
              </DialogTitle>
              <DialogDescription className="text-[#A1A1AA] text-sm">
                A secure invite link has been generated. Share it via QR code or send directly to their phone.
              </DialogDescription>
            </DialogHeader>

            {/* Tab switcher */}
            <div className="flex gap-1 bg-[#09090B] rounded-sm p-1 mt-2">
              <button
                onClick={() => setOnboardTab('qr')}
                data-testid="onboard-tab-qr"
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold uppercase tracking-wide rounded-sm transition-all ${
                  onboardTab === 'qr'
                    ? 'bg-[#CCFF00] text-black'
                    : 'text-[#A1A1AA] hover:text-[#FAFAFA]'
                }`}
              >
                <QrCode className="w-3.5 h-3.5" />
                QR Code
              </button>
              <button
                onClick={() => setOnboardTab('link')}
                data-testid="onboard-tab-link"
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold uppercase tracking-wide rounded-sm transition-all ${
                  onboardTab === 'link'
                    ? 'bg-[#CCFF00] text-black'
                    : 'text-[#A1A1AA] hover:text-[#FAFAFA]'
                }`}
              >
                <Link className="w-3.5 h-3.5" />
                Share Link
              </button>
            </div>

            {onboardTab === 'qr' && (
              <div className="flex flex-col items-center gap-4 py-4" data-testid="qr-tab-content">
                <div className="bg-white p-4 rounded-sm">
                  <QRCodeSVG
                    value={onboardModal.inviteUrl}
                    size={200}
                    level="M"
                    data-testid="qr-code"
                  />
                </div>
                <p className="text-xs text-[#A1A1AA] text-center">
                  Ask {onboardModal.user.name} to scan this with their phone camera.
                  They'll be logged in and prompted to set their password.
                </p>
                <p className="text-xs text-[#52525B] text-center">Link expires in 48 hours</p>
              </div>
            )}

            {onboardTab === 'link' && (
              <div className="flex flex-col gap-3 py-4" data-testid="link-tab-content">
                <div className="bg-[#09090B] border border-[#27272A] rounded-sm p-3">
                  <p className="text-xs text-[#A1A1AA] break-all font-mono leading-relaxed">
                    {onboardModal.inviteUrl}
                  </p>
                </div>
                <Button
                  onClick={copyInviteLink}
                  data-testid="copy-invite-link"
                  className="w-full bg-[#27272A] text-[#FAFAFA] hover:bg-[#3F3F46] font-bold uppercase text-xs tracking-wide rounded-sm h-10"
                >
                  <Copy className="w-3.5 h-3.5 mr-2" />
                  Copy Link
                </Button>
                <Button
                  onClick={shareWhatsApp}
                  data-testid="share-whatsapp"
                  className="w-full bg-[#25D366] text-white hover:bg-[#20BD5C] font-bold uppercase text-xs tracking-wide rounded-sm h-10"
                >
                  <svg className="w-3.5 h-3.5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Share via WhatsApp
                </Button>
                <p className="text-xs text-[#52525B] text-center">Link expires in 48 hours</p>
              </div>
            )}

            <DialogFooter>
              <Button
                onClick={() => setOnboardModal(null)}
                variant="outline"
                data-testid="close-onboard-modal"
                className="w-full border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]"
              >
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* User Form Dialog */}
      <Dialog open={showUserForm} onOpenChange={setShowUserForm}>
        <DialogContent className="bg-[#18181B] border-[#27272A] max-w-md" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#FAFAFA] font-heading uppercase">Create New User</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Add a new athlete or coach to the platform
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createUser} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="user-name" className="text-[#FAFAFA] uppercase text-xs font-semibold">Name</Label>
              <Input
                id="user-name"
                data-testid="user-name-input"
                value={userFormData.name}
                onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
                required
                className="bg-[#09090B] border-[#27272A] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-username" className="text-[#FAFAFA] uppercase text-xs font-semibold">Username</Label>
              <Input
                id="user-username"
                data-testid="user-email-input"
                type="text"
                value={userFormData.username}
                onChange={(e) => setUserFormData({ ...userFormData, username: e.target.value })}
                required
                autoCapitalize="none"
                autoCorrect="off"
                placeholder="e.g. fanus or member01"
                className="bg-[#09090B] border-[#27272A] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-password" className="text-[#FAFAFA] uppercase text-xs font-semibold">Temporary Password</Label>
              <Input
                id="user-password"
                data-testid="user-password-input"
                type="password"
                value={userFormData.password}
                onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                required
                className="bg-[#09090B] border-[#27272A] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-role" className="text-[#FAFAFA] uppercase text-xs font-semibold">Role</Label>
              <select
                id="user-role"
                data-testid="user-role-select"
                value={userFormData.role}
                onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value })}
                className="w-full bg-[#09090B] border border-[#27272A] text-white rounded-sm h-10 px-3 focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] focus:outline-none"
              >
                <option value="athlete">Athlete</option>
                <option value="coach">Coach</option>
                <option value="head_coach">Head Coach</option>
              </select>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowUserForm(false)}
                data-testid="cancel-user-form"
                className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-user-form"
                className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase"
              >
                Create User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* WOD Form Dialog */}
      <Dialog open={showWODForm} onOpenChange={(open) => !open && cancelWODEdit()}>
        <DialogContent className="bg-[#18181B] border-[#27272A] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="wod-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#FAFAFA] font-heading uppercase">
              {editingWOD ? 'Edit WOD' : 'Create New WOD'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={createOrUpdateWOD} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="wod-title" className="text-[#FAFAFA] uppercase text-xs font-semibold">Title</Label>
              <Input
                id="wod-title"
                data-testid="wod-title-input"
                value={wodFormData.title}
                onChange={(e) => setWodFormData({ ...wodFormData, title: e.target.value })}
                required
                className="bg-[#09090B] border-[#27272A] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wod-date" className="text-[#FAFAFA] uppercase text-xs font-semibold">Date</Label>
              <Input
                id="wod-date"
                data-testid="wod-date-input"
                type="date"
                value={wodFormData.date}
                onChange={(e) => setWodFormData({ ...wodFormData, date: e.target.value })}
                required
                className="bg-[#09090B] border-[#27272A] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wod-description" className="text-[#FAFAFA] uppercase text-xs font-semibold">Description</Label>
              <Textarea
                id="wod-description"
                data-testid="wod-description-input"
                value={wodFormData.description}
                onChange={(e) => setWodFormData({ ...wodFormData, description: e.target.value })}
                placeholder="Enter WOD details...&#10;&#10;Example:&#10;3 Rounds For Time:&#10;- 400m Run&#10;- 21 Kettlebell Swings (53/35 lbs)&#10;- 12 Pull-ups"
                required
                rows={10}
                className="bg-[#09090B] border-[#27272A] text-white placeholder:text-[#52525B] focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] font-mono text-sm resize-y"
              />
              <p className="text-xs text-[#A1A1AA]">
                Tip: Use line breaks and dashes for better formatting
              </p>
            </div>

            {/* PR Movement Link (optional) */}
            <div className="space-y-3 border border-[#27272A] rounded-sm p-4 bg-[#09090B]">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-[#FAFAFA] uppercase text-xs font-semibold flex items-center gap-2">
                    <Trophy className="w-3 h-3 text-[#CCFF00]" />
                    Link a PR Movement
                  </Label>
                  <p className="text-xs text-[#52525B] mt-0.5">Optional — athletes can log their score from the whiteboard</p>
                </div>
                {wodFormData.pr_movement_id && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setWodFormData(prev => ({ ...prev, pr_movement_id: '' }))}
                    className="border-[#27272A] text-[#52525B] hover:text-[#EF4444] text-xs h-7"
                  >
                    <X className="w-3 h-3 mr-1" /> Unlink
                  </Button>
                )}
              </div>

              {/* Show linked movement name */}
              {wodFormData.pr_movement_id && (
                <p className="text-xs text-[#CCFF00] font-semibold">
                  Linked: {movements.find(m => m.id === wodFormData.pr_movement_id)?.name}
                </p>
              )}

              {/* Select existing */}
              {!showNewMovementInline && (
                <div className="flex gap-2">
                  <select
                    data-testid="wod-pr-movement-select"
                    value={wodFormData.pr_movement_id}
                    onChange={(e) => setWodFormData(prev => ({ ...prev, pr_movement_id: e.target.value }))}
                    className="flex-1 bg-[#18181B] border border-[#27272A] text-white rounded-sm h-9 px-3 text-sm focus:border-[#CCFF00] focus:outline-none"
                  >
                    <option value="">— None —</option>
                    {movements.map(m => (
                      <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setShowNewMovementInline(true)}
                    className="border-[#27272A] text-[#A1A1AA] hover:text-[#CCFF00] text-xs whitespace-nowrap"
                  >
                    <Plus className="w-3 h-3 mr-1" /> New
                  </Button>
                </div>
              )}

              {/* Create new movement inline */}
              {showNewMovementInline && (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      data-testid="inline-movement-name"
                      value={inlineMovement.name}
                      onChange={(e) => setInlineMovement(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Thruster 1RM"
                      className="bg-[#18181B] border-[#27272A] text-white h-9 text-sm flex-1"
                    />
                    <select
                      value={inlineMovement.unit}
                      onChange={(e) => setInlineMovement(prev => ({ ...prev, unit: e.target.value }))}
                      className="bg-[#18181B] border border-[#27272A] text-white rounded-sm h-9 px-2 text-sm"
                    >
                      <option value="kg">kg</option>
                      <option value="lbs">lbs</option>
                      <option value="reps">reps</option>
                      <option value="seconds">sec</option>
                      <option value="minutes">min</option>
                      <option value="meters">m</option>
                      <option value="calories">cal</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={createInlineMovement} className="bg-[#CCFF00] text-black h-7 text-xs font-bold">
                      Create & Link
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setShowNewMovementInline(false)} className="h-7 text-xs border-[#27272A] text-[#A1A1AA]">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              {editingWOD && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setDeleteWODDialog(editingWOD); cancelWODEdit(); }}
                  data-testid="delete-wod-form"
                  className="border-[#27272A] text-[#EF4444] hover:bg-[#EF4444]/10 mr-auto"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={cancelWODEdit}
                data-testid="cancel-wod-form"
                className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-wod-form"
                className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase"
              >
                {editingWOD ? 'Save Changes' : 'Create WOD'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Class Form Dialog */}
      <Dialog open={showClassForm} onOpenChange={(open) => { if (!open) { setShowClassForm(false); setEditingClassId(null); } }}>
        <DialogContent className="bg-[#18181B] border-[#27272A] max-w-md max-h-[90vh] overflow-y-auto" data-testid="class-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#FAFAFA] font-heading uppercase">
              {classFormMode === 'edit' ? 'Edit Class' : classFormMode === 'recurring' ? 'Create Recurring Classes' : 'Schedule Class'}
            </DialogTitle>
          </DialogHeader>

          {classFormMode === 'recurring' ? (
            <form onSubmit={createRecurringClasses} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Title</Label>
                <Input value={recurringFormData.title} onChange={(e) => setRecurringFormData({ ...recurringFormData, title: e.target.value })} required className="bg-[#09090B] border-[#27272A] text-white" placeholder="e.g., Monday Morning" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Days of Week</Label>
                <div className="flex flex-wrap gap-2">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => (
                    <button key={i} type="button" onClick={() => {
                      const days = recurringFormData.days_of_week.includes(i)
                        ? recurringFormData.days_of_week.filter(d => d !== i)
                        : [...recurringFormData.days_of_week, i];
                      setRecurringFormData({ ...recurringFormData, days_of_week: days });
                    }}
                      className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase ${recurringFormData.days_of_week.includes(i) ? 'bg-[#CCFF00] text-black' : 'bg-[#27272A] text-[#A1A1AA]'}`}
                    >{day}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Start Time</Label>
                  <Input type="time" value={recurringFormData.start_time} onChange={(e) => setRecurringFormData({ ...recurringFormData, start_time: e.target.value })} required className="bg-[#09090B] border-[#27272A] text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">End Time</Label>
                  <Input type="time" value={recurringFormData.end_time} onChange={(e) => setRecurringFormData({ ...recurringFormData, end_time: e.target.value })} required className="bg-[#09090B] border-[#27272A] text-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">From Date</Label>
                  <Input type="date" value={recurringFormData.start_date} onChange={(e) => setRecurringFormData({ ...recurringFormData, start_date: e.target.value })} required className="bg-[#09090B] border-[#27272A] text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Until Date</Label>
                  <Input type="date" value={recurringFormData.end_date} onChange={(e) => setRecurringFormData({ ...recurringFormData, end_date: e.target.value })} required className="bg-[#09090B] border-[#27272A] text-white" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Max Capacity</Label>
                <Input type="number" min="1" value={recurringFormData.max_capacity} onChange={(e) => setRecurringFormData({ ...recurringFormData, max_capacity: parseInt(e.target.value) })} required className="bg-[#09090B] border-[#27272A] text-white" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowClassForm(false)} className="border-[#27272A] text-[#A1A1AA]">Cancel</Button>
                <Button type="submit" disabled={recurringFormData.days_of_week.length === 0} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase">Create All</Button>
              </DialogFooter>
            </form>
          ) : (
            <form onSubmit={createClass} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Title</Label>
                <Input value={classFormData.title} onChange={(e) => setClassFormData({ ...classFormData, title: e.target.value })} required className="bg-[#09090B] border-[#27272A] text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Date</Label>
                <Input type="date" value={classFormData.date} onChange={(e) => setClassFormData({ ...classFormData, date: e.target.value })} required className="bg-[#09090B] border-[#27272A] text-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Start Time</Label>
                  <Input type="time" value={classFormData.start_time} onChange={(e) => handleStartTimeChange(e.target.value)} required className="bg-[#09090B] border-[#27272A] text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">End Time</Label>
                  <Input type="time" value={classFormData.end_time} onChange={(e) => setClassFormData({ ...classFormData, end_time: e.target.value })} required className="bg-[#09090B] border-[#27272A] text-white" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Max Capacity</Label>
                <Input type="number" min="1" value={classFormData.max_capacity} onChange={(e) => setClassFormData({ ...classFormData, max_capacity: parseInt(e.target.value) })} required className="bg-[#09090B] border-[#27272A] text-white" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setShowClassForm(false); setEditingClassId(null); }} className="border-[#27272A] text-[#A1A1AA]">Cancel</Button>
                <Button type="submit" className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase">
                  {editingClassId ? 'Save Changes' : 'Schedule'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* User Attendance Dialog */}
      <Dialog open={!!selectedUserAttendance} onOpenChange={() => setSelectedUserAttendance(null)}>
        <DialogContent className="bg-[#18181B] border-[#27272A] max-w-lg max-h-[80vh] overflow-y-auto" data-testid="attendance-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#FAFAFA] font-heading uppercase">Attendance History</DialogTitle>
            {userAttendance.user_name && (
              <div className="mt-2">
                <p className="text-[#FAFAFA] font-semibold text-lg">{userAttendance.user_name}</p>
                <p className="text-[#A1A1AA] text-sm">{userAttendance.user_email}</p>
              </div>
            )}
          </DialogHeader>

          {/* Stats Summary */}
          {userAttendance.total_bookings !== undefined && (
            <div className="grid grid-cols-3 gap-3 mt-4" data-testid="attendance-stats">
              <div className="bg-[#09090B] border border-[#27272A] rounded-sm p-3 text-center">
                <p className="text-2xl font-bold text-[#CCFF00]">{userAttendance.total_bookings}</p>
                <p className="text-xs text-[#A1A1AA] uppercase tracking-wide">Bookings</p>
              </div>
              <div className="bg-[#09090B] border border-[#27272A] rounded-sm p-3 text-center">
                <p className="text-2xl font-bold text-[#22C55E]">{userAttendance.total_attended}</p>
                <p className="text-xs text-[#A1A1AA] uppercase tracking-wide">Attended</p>
              </div>
              <div className="bg-[#09090B] border border-[#27272A] rounded-sm p-3 text-center">
                <p className="text-2xl font-bold text-[#FAFAFA]">{userAttendance.attendance_rate}%</p>
                <p className="text-xs text-[#A1A1AA] uppercase tracking-wide">Rate</p>
              </div>
            </div>
          )}

          <div className="space-y-2 mt-4">
            {(!userAttendance.classes || userAttendance.classes?.length === 0) ? (
              <p className="text-[#A1A1AA] text-sm">No attendance records</p>
            ) : (
              userAttendance.classes.map((cls, index) => (
                <div key={cls.booking_id || index} className="p-3 bg-[#09090B] rounded-sm border border-[#27272A] flex items-center justify-between">
                  <div>
                    <p className="text-[#FAFAFA] font-semibold">{cls.title}</p>
                    <p className="text-[#A1A1AA] text-sm">
                      {new Date(cls.date).toLocaleDateString()} {cls.start_time && `\u2022 ${cls.start_time}`}
                      {cls.end_time && ` - ${cls.end_time}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {cls.checked_in ? (
                      <span className="flex items-center gap-1 text-[#22C55E] text-xs font-semibold">
                        <Check className="w-3 h-3" />
                        Attended
                      </span>
                    ) : (
                      <span className="text-[#F97316] text-xs font-semibold">Booked</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUserDialog} onOpenChange={(open) => !open && setEditUserDialog(null)}>
        <DialogContent className="bg-[#18181B] border-[#27272A] max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#FAFAFA] font-heading uppercase">Edit User</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Update name, username or change role</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Name</Label>
              <Input
                data-testid="edit-user-name"
                value={editUserData.name}
                onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })}
                className="bg-[#09090B] border-[#27272A] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Username</Label>
              <Input
                data-testid="edit-user-username"
                value={editUserData.username}
                onChange={(e) => setEditUserData({ ...editUserData, username: e.target.value })}
                placeholder="e.g. fanus or member01"
                autoCapitalize="none"
                autoCorrect="off"
                className="bg-[#09090B] border-[#27272A] text-white"
              />
              <p className="text-xs text-[#52525B]">This is what the member types to log in</p>
            </div>
            <div className="space-y-2">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Role</Label>
              <select
                data-testid="edit-user-role"
                value={editUserData.role}
                onChange={(e) => setEditUserData({ ...editUserData, role: e.target.value })}
                className="w-full bg-[#09090B] border border-[#27272A] text-white rounded-sm h-10 px-3 focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] focus:outline-none"
              >
                <option value="athlete">Athlete</option>
                <option value="coach">Coach</option>
                <option value="head_coach">Head Coach</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Birthday (MM-DD)</Label>
              <Input
                data-testid="edit-user-birthday"
                value={editUserData.birthday}
                onChange={(e) => setEditUserData({ ...editUserData, birthday: e.target.value })}
                placeholder="e.g. 03-15 for March 15"
                className="bg-[#09090B] border-[#27272A] text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserDialog(null)} className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]">Cancel</Button>
            <Button onClick={saveEditUser} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <AlertDialog open={!!deleteUserDialog} onOpenChange={(open) => !open && setDeleteUserDialog(null)}>
        <AlertDialogContent className="bg-[#18181B] border-[#27272A]" data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#FAFAFA] font-heading uppercase">Delete User</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              Are you sure you want to delete this user? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]" data-testid="cancel-delete-user">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteUser}
              data-testid="confirm-delete-user"
              className="bg-[#EF4444] text-white hover:bg-[#DC2626]"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordDialog} onOpenChange={(open) => !open && setResetPasswordDialog(null)}>
        <DialogContent className="bg-[#18181B] border-[#27272A] max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#FAFAFA] font-heading uppercase">Reset Password</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Enter a new password for this user
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-[#FAFAFA] uppercase text-xs font-semibold">New Password</Label>
              <Input
                id="new-password"
                data-testid="new-password-input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-[#09090B] border-[#27272A] text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResetPasswordDialog(null)}
              data-testid="cancel-reset-password"
              className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]"
            >
              Cancel
            </Button>
            <Button
              onClick={resetPassword}
              data-testid="confirm-reset-password"
              className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase"
            >
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete WOD Dialog */}
      <AlertDialog open={!!deleteWODDialog} onOpenChange={(open) => !open && setDeleteWODDialog(null)}>
        <AlertDialogContent className="bg-[#18181B] border-[#27272A]" data-testid="delete-wod-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#FAFAFA] font-heading uppercase">Delete WOD</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              Are you sure you want to delete this WOD? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]" data-testid="cancel-delete-wod">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteWOD}
              data-testid="confirm-delete-wod"
              className="bg-[#EF4444] text-white hover:bg-[#DC2626]"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Class Dialog */}
      <AlertDialog open={!!deleteClassDialog} onOpenChange={(open) => !open && setDeleteClassDialog(null)}>
        <AlertDialogContent className="bg-[#18181B] border-[#27272A]" data-testid="delete-class-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#FAFAFA] font-heading uppercase">Cancel Class</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              Are you sure you want to cancel this class? All bookings will be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]" data-testid="cancel-delete-class">
              Keep Class
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteClass}
              data-testid="confirm-delete-class"
              className="bg-[#EF4444] text-white hover:bg-[#DC2626]"
            >
              Cancel Class
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
